import { useApp, useAuth, useCollection } from '@hb/react'
import {
  AdminAssessmentData,
  AdminUserData,
  arrayToObject,
  getFullName,
  getLogInfo,
  getReverseName,
  insurersCollection,
  LogInfo,
  mergeData,
  MIDWIVES,
  PopulatedUser,
  populateInsurancePlans,
  PracticeUserData,
  User,
  USER_INVITES,
  USER_ROLES,
  UserInvite,
  USERS,
  USERS_ADMIN,
} from '@hb/shared'
import 'firebase/firestore'
import { useMemo } from 'react'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import { PopulatedUserData } from '../../../types/populated'
import { usePatientClaims } from './usePatientClaims'

export const useLogInfo = (log?: AdminAssessmentData['log']) =>
  useMemo<LogInfo>(() => getLogInfo(log), [log])
export function usePopulatedUser(
  id?: string | null,
  disabled?: boolean,
  practiceId?: string | null,
): PopulatedUserData {
  const { authUser, loading: authLoading, admin } = useAuth()
  const { appName } = useApp()
  const { items: insurers } = useCollection(insurersCollection)
  const userId = useMemo(() => (authUser ? id : null), [authUser, id])
  const { data: userItem, loading: uLoading, ref: userItemRef } = useDocument<User>(USERS, userId)
  const {
    data: adminItem,
    loading: aLoading,
    ref: adminPatientRef,
  } = useDocument<AdminUserData>(USERS_ADMIN, appName === 'providers-app' || !admin ? null : userId)
  const {
    data: inviteItem,
    loading: iLoading,
    ref: inviteRef,
  } = useDocument<UserInvite>(USER_INVITES, userId)

  const {
    data: practiceItem,
    loading: practiceItemLoading,
    ref: practicePatientRef,
  } = useDocument<PracticeUserData>(
    `${MIDWIVES}/${practiceId}/${USER_ROLES}`,
    practiceId ? userId : null,
  )

  const loading = useMemo(() => {
    if (authLoading || aLoading || uLoading || iLoading || practiceItemLoading) {
      return true
    }
    return false
  }, [authLoading, aLoading, uLoading, iLoading, practiceItemLoading])
  const {
    item,
    ref: patientRef,
    isInvite,
  } = useMemo(() => {
    if (uLoading || disabled) return { item: null, ref: null, isInvite: false }
    if (userItem) {
      return { item: userItem, ref: userItemRef, isInvite: false }
    }
    return { item: inviteItem, ref: inviteRef, isInvite: true }
  }, [userItem, inviteItem, disabled, inviteRef, uLoading, userItemRef])
  const { data: claims } = usePatientClaims(id)
  const user = useMemo<PopulatedUser | null>(() => {
    if (!id || !item) return null
    const insurancePlans = populateInsurancePlans(
      item.insurancePlans ?? {},
      arrayToObject(insurers) ?? {},
    )
    const mergedPartnerInfo = mergeData(
      item.partnerInfo?.data ?? {},
      item.partnerInfo?.corrections ?? {},
    )
    const mergedGynData = mergeData(item.gyn?.data ?? {}, item.gyn?.corrections ?? {})
    return {
      ...item,
      ...adminItem,
      files: item?.files ?? {},
      adminFiles: adminItem?.files ?? {},
      mergedPartnerInfo,
      gyn: {
        ...item.gyn,
        mergedData: mergedGynData,
      },
      adminGynNextAction: adminItem?.gynNextAction,
      practiceGynNextAction: practiceItem?.gynNextAction,
      drafts: adminItem?.drafts,
      practiceDrafts: practiceItem?.drafts,
      patientNote: appName === 'providers-app' ? practiceItem?.patientNote : adminItem?.patientNote,
      adminAlerts: adminItem?.alerts,
      practiceAlerts: practiceItem?.alerts,
      isInvite,
      insurancePlans,
      insuranceProvider: insurancePlans?.primary?.insuranceProvider ?? null,
      name: getFullName(item),
      reverseName: getReverseName(item),
      claims: claims ?? undefined,
      id,
    }
  }, [item, adminItem, claims, id, isInvite, practiceItem, appName, insurers])
  return useMemo(
    () => ({
      user,
      adminItem,
      userItem,
      loading,
      isInvite,
      patientRef,
      practicePatientRef,
      adminPatientRef,
    }),
    [user, loading, patientRef, adminPatientRef, practicePatientRef, adminItem, isInvite, userItem],
  )
}
